/**
 * BurnRateCell — burn-rate readout for the Projects table.
 *
 * A real number (including 0) renders the track with a 0–100% fill; a missing value renders a muted
 * "N/A" with no track at all. Rendering an empty track for both made "zero burn" and "no data"
 * indistinguishable (PRJ-02), so the two states must not share a shape.
 */
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';

/**
 * Thresholds: under-budget → good, approaching → warning, at/over → critical.
 * Colours come from the `--color-burnrate-*` tokens in globals.css `@theme` — never a stock
 * Tailwind palette class. The bar uses the base step (a large block, 3:1 suffices); the label
 * beside it uses the darker `-text` step because it is normal text at 4.5:1.
 */
function burnRateBarClass(pct: number): string {
  if (pct < 70) return '[&>div]:bg-burnrate-good';
  if (pct < 90) return '[&>div]:bg-burnrate-warning';
  return '[&>div]:bg-burnrate-critical';
}

function burnRateTextClass(pct: number): string {
  if (pct < 70) return 'text-burnrate-good-text';
  if (pct < 90) return 'text-burnrate-warning-text';
  return 'text-burnrate-critical-text';
}

export interface BurnRateCellProps {
  /** Burn rate percentage, or null when the project has no SOW hours to burn against. */
  pct: number | null;
}

export function BurnRateCell({ pct }: BurnRateCellProps): JSX.Element {
  if (pct == null) {
    return (
      <span
        className="text-xs font-medium text-text-placeholder"
        title="Burn rate not available for this project"
      >
        N/A
      </span>
    );
  }
  const width = Math.min(Math.max(pct, 0), 100);
  return (
    <div className="flex items-center gap-2">
      <Progress
        value={width}
        className={cn('h-1.5 w-24', burnRateBarClass(pct))}
        aria-label={`Burn rate: ${Math.round(pct)}%`}
      />
      <span className={cn('w-10 shrink-0 text-right text-xs font-medium', burnRateTextClass(pct))}>
        {`${Math.round(pct)}%`}
      </span>
    </div>
  );
}

export default BurnRateCell;
